"use client";

import { StoryblokComponentList } from "@trydig/storyblok";
import { clsx } from "@trydig/ui";
import { forwardRef, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { useHideBreadcrumbs } from "@/components/ui/Breadcrumbs/visibility";
import { Container } from "@/components/ui/Container";
import { PathBreadcrumbs } from "@/components/ui/PathBreadcrumbs";
import { Scrollbar } from "@/components/ui/Scrollbar";
import { Typography } from "@/components/ui/Typography";
import type { PageHeaderStoryblok } from "@/generated/sb/sb-types";
import { Link as NextLink } from "@/i18n/routing";

type BreadcrumbItem = {
	label: string;
	href?: string;
};

export interface PageHeaderProps {
	title?: string;
	description?: string;
	/**
	 * Explicit breadcrumb trail. When omitted, breadcrumbs are derived from the
	 * current URL slug — see `PathBreadcrumbs`. Provide this only when the
	 * trail doesn't follow the URL (e.g. an FAQ item under a known parent).
	 */
	breadcrumbs?: BreadcrumbItem[];
	/** Override label for the last derived breadcrumb segment. Defaults to `title`. */
	breadcrumbTitle?: string;
	size?: "level-1" | "level-2" | "level-3";
	subcategories?: PageHeaderStoryblok["subcategories"];
	collections?: PageHeaderStoryblok["collections"];
	action?: ReactNode;
	bg?: string;
	className?: string;
	/** Show the bottom divider. Defaults to true. */
	divider?: boolean;
	preview: boolean;
	darkMode?: boolean;
}

function usePagedScroll() {
	const ref = useRef<HTMLDivElement>(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;

		const update = () => {
			if (el.clientWidth === 0) return;
			const pages = Math.max(1, Math.ceil(el.scrollWidth / el.clientWidth));
			setTotalPages(pages);
		};

		update();
		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;

		const handleScroll = () => {
			if (el.clientWidth === 0) return;
			const scrollLeft = el.scrollLeft;
			const page = Math.round(scrollLeft / el.clientWidth) + 1;
			setCurrentPage(page);
		};

		el.addEventListener("scroll", handleScroll);
		return () => el.removeEventListener("scroll", handleScroll);
	}, []);

	const handlePrev = useCallback(() => {
		const el = ref.current;
		if (!el) return;
		const next = Math.max(1, currentPage - 1);
		setCurrentPage(next);
		el.scrollTo({ left: (next - 1) * el.clientWidth, behavior: "smooth" });
	}, [currentPage]);

	const handleNext = useCallback(() => {
		const el = ref.current;
		if (!el) return;
		const next = Math.min(totalPages, currentPage + 1);
		setCurrentPage(next);
		el.scrollTo({ left: (next - 1) * el.clientWidth, behavior: "smooth" });
	}, [currentPage, totalPages]);

	return { ref, currentPage, totalPages, handlePrev, handleNext };
}

export const PageHeader = forwardRef<HTMLDivElement, PageHeaderProps>(
	(
		{
			title,
			description,
			breadcrumbs,
			breadcrumbTitle,
			size = "level-1",
			subcategories,
			collections,
			action,
			className,
			divider = true,
			preview,
		},
		ref,
	) => {
		const subcatScroll = usePagedScroll();
		const collectScroll = usePagedScroll();
		const hideBreadcrumbs = useHideBreadcrumbs();

		return (
			<section ref={ref} className={twMerge("py-grid-section-vertical-md", className)}>
				<Container
					className={twMerge(
						"flex flex-col items-center",
						size === "level-1" ? "gap-grid-section-vertical-lg" : "gap-grid-section-vertical-md",
					)}
				>
					<div className="flex w-full flex-col items-start gap-grid-section-vertical-lg">
						{/* Title section */}
						<div className="flex w-full flex-col items-start gap-grid-section-vertical-md">
							{!hideBreadcrumbs &&
								(breadcrumbs ? (
									// A lone crumb (e.g. a top-level category) has no navigational value, so hide it.
									breadcrumbs.length > 1 && (
										<Breadcrumbs items={breadcrumbs} size="large" linkAs={NextLink} />
									)
								) : (
									<PathBreadcrumbs title={breadcrumbTitle ?? title} size="large" />
								))}

							{size === "level-1" && title && (
								<div className="grid w-full grid-cols-12 gap-grid-grid-gutter-md">
									<Typography
										variant="heading-display-lg"
										as="h1"
										className={clsx("col-span-full lg:col-span-8 text-text-dark-text-1")}
									>
										{title}
									</Typography>
								</div>
							)}

							{size === "level-2" && title && (
								<div className="grid w-full grid-cols-12 gap-grid-grid-gutter-md">
									<Typography
										variant="heading-display-md"
										as="h1"
										className={clsx("col-span-full lg:col-span-6 text-text-dark-text-1")}
									>
										{title}
									</Typography>
									{description && (
										<div className="col-span-full lg:col-span-6 flex items-center">
											<Typography
												variant="heading-lg"
												className={clsx("text-text-dark-text-1 leading-[1.25]")}
											>
												{description}
											</Typography>
										</div>
									)}
								</div>
							)}

							{size === "level-3" && (title || action || description) && (
								<>
									{title && (
										<div className="grid w-full grid-cols-12 gap-grid-grid-gutter-md">
											<Typography
												variant="heading-display-md"
												as="h1"
												className={clsx("col-span-full lg:col-span-8 text-text-dark-text-1")}
											>
												{title}
											</Typography>
											{action && (
												<div className="col-span-full lg:col-span-4 flex items-start lg:justify-end">
													{action}
												</div>
											)}
										</div>
									)}
									{description && (
										<div className="grid w-full grid-cols-12 gap-grid-grid-gutter-md">
											<Typography
												variant="body-sm-reg"
												className={clsx(
													"col-span-full lg:col-span-8 text-text-dark-text-1 leading-[1.5]",
												)}
											>
												{description}
											</Typography>
										</div>
									)}
								</>
							)}
						</div>

						{/* Description for Level 1 (at content level for larger gap from title) */}
						{size === "level-1" && description && (
							<div className="grid w-full grid-cols-12 gap-grid-grid-gutter-md">
								<Typography
									variant="body-sm-reg"
									className={clsx(
										"col-span-full lg:col-span-8 text-text-dark-text-1 leading-[1.5]",
									)}
								>
									{description}
								</Typography>
							</div>
						)}

						{/* Subcategories */}
						{subcategories && subcategories.length > 0 && (
							<div className="flex w-full flex-col gap-grid-section-vertical-sm">
								<div className="relative w-full overflow-hidden">
									<div
										ref={subcatScroll.ref}
										className="flex gap-grid-grid-gutter-md overflow-x-auto [scrollbar-width:none]"
									>
										<StoryblokComponentList bloks={subcategories} preview={preview} />
									</div>
									<div
										className={twMerge(
											"pointer-events-none absolute right-0 top-0 h-10 w-16 bg-gradient-to-r from-transparent",
											// SECTION_BG_GRADIENT_TO[bg],
										)}
									/>
								</div>
								{subcatScroll.totalPages > 1 && (
									<Scrollbar
										current={subcatScroll.currentPage}
										total={subcatScroll.totalPages}
										onPrev={subcatScroll.handlePrev}
										onNext={subcatScroll.handleNext}
									/>
								)}
							</div>
						)}

						{/* Collections */}
						{collections && collections.length > 0 && (
							<div className="flex w-full flex-col gap-grid-section-vertical-sm">
								<div
									ref={collectScroll.ref}
									className="flex gap-grid-grid-gutter-md overflow-x-auto [scrollbar-width:none]"
								>
									<StoryblokComponentList bloks={collections} preview={preview} />
								</div>
								{collectScroll.totalPages > 1 && (
									<Scrollbar
										current={collectScroll.currentPage}
										total={collectScroll.totalPages}
										onPrev={collectScroll.handlePrev}
										onNext={collectScroll.handleNext}
									/>
								)}
							</div>
						)}
					</div>

					{/* Divider — hidden for level-2 (per design), when only breadcrumbs are shown, or when explicitly disabled */}
					{divider &&
						size !== "level-2" &&
						(title || description || subcategories || collections) && (
							<div className="h-px w-full bg-border-medium" />
						)}
				</Container>
			</section>
		);
	},
);

PageHeader.displayName = "PageHeader";
